import { useState } from "react";
import {
  CalendarSearch,
  MapPin,
  MessageCircle,
  Check,
  Clock,
  Zap,
  User,
  IdCard,
  CalendarDays,
  Phone,
  ListChecks,
  ClipboardCheck,
} from "lucide-react";
import { WHATSAPP_URL } from "../lib/whatsapp";
import { StepList } from "../components/StepList";
import { useSeo } from "../lib/seo";
import { BookingProofSection } from "../components/BookingProofSection";
import { FormField, formInputClass } from "../components/forms/fields";
import { sendContactForm, SUCCESS_MESSAGE } from "../lib/contactForm";

const benefits = [
  { icon: CalendarSearch, label: "Earlier test date searches" },
  { icon: MapPin, label: "UK test centres" },
  { icon: Zap, label: "Quick communication" },
  { icon: Check, label: "Simple booking support" },
  { icon: Clock, label: "Saves time and stress" },
];

const steps = [
  { title: "Get In Touch", text: "Send us your requirements." },
  { title: "We Search", text: "We search for suitable earlier availability." },
  { title: "Book & Confirm", text: "We assist with arranging a suitable test date." },
  { title: "You're Ready", text: "You can focus on preparing for your test." },
];

const beforeYouContact = [
  { icon: User, label: "Full name" },
  { icon: IdCard, label: "Driving licence details" },
  { icon: CalendarDays, label: "Existing test date (if applicable)" },
  { icon: MapPin, label: "Preferred test centres / areas" },
  { icon: CalendarSearch, label: "Preferred dates" },
  { icon: Phone, label: "Contact number" },
];

const initialForm = {
  testCentres: "",
  licenceNumber: "",
  email: "",
  theoryNumber: "",
  phone: "",
  suitableDates: "",
  unsuitableDates: "",
  latestDate: "",
};

const TestEnquiryForm = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const validate = () => {
    const e = {};
    if (!form.testCentres.trim()) e.testCentres = "Please tell us your preferred test centre(s).";
    if (form.licenceNumber.trim().length < 5) e.licenceNumber = "Please enter your driving licence number.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (form.theoryNumber.trim().length < 5) e.theoryNumber = "Please enter your theory test pass number.";
    if (!/^[0-9+\s]{7,}$/.test(form.phone)) e.phone = "Please enter a valid phone number.";
    if (!form.suitableDates.trim()) e.suitableDates = "Please tell us your suitable dates.";
    if (!form.latestDate) e.latestDate = "Please choose the latest date you'd like your test by.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    if (sending) return;
    if (!validate()) return;
    setSending(true);
    setSendError("");
    try {
      await sendContactForm({
        subject: "New EDT Driving Test Enquiry",
        from_name: "Driving Test Applicant",
        botcheck: honeypot,
        fields: {
          email: form.email,
          Phone: form.phone,
          "Preferred Test Centre(s)": form.testCentres,
          "Driving Licence Number": form.licenceNumber,
          "Theory Test Pass Number": form.theoryNumber,
          "Latest Date Wanted": form.latestDate,
          "Suitable Dates": form.suitableDates,
          "Unsuitable Dates": form.unsuitableDates,
        },
      });
      setSubmitted(true);
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="test-form-success-message" className="border border-[rgba(201,162,75,0.3)] px-6 py-14 text-center">
        <ClipboardCheck size={26} className="mx-auto text-[#c9a24b]" />
        <p className="mt-4 text-sm text-[#f2ede3]">{SUCCESS_MESSAGE}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-10 grid gap-6 sm:grid-cols-2" data-testid="test-enquiry-form">
      <input type="text" name="botcheck" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <FormField label="Preferred Test Centre(s)" error={errors.testCentres} testId="test-form-centres">
        <input className={formInputClass} value={form.testCentres} onChange={update("testCentres")} data-testid="test-form-centres-input" />
      </FormField>
      <FormField label="Driving Licence Number" error={errors.licenceNumber} testId="test-form-licence">
        <input className={formInputClass} value={form.licenceNumber} onChange={update("licenceNumber")} data-testid="test-form-licence-input" />
      </FormField>
      <FormField label="Email Address" error={errors.email} testId="test-form-email">
        <input type="email" className={formInputClass} value={form.email} onChange={update("email")} data-testid="test-form-email-input" />
      </FormField>
      <FormField label="Theory Test Pass Number" error={errors.theoryNumber} testId="test-form-theory">
        <input className={formInputClass} value={form.theoryNumber} onChange={update("theoryNumber")} data-testid="test-form-theory-input" />
      </FormField>
      <FormField label="Phone Number" error={errors.phone} testId="test-form-phone">
        <input className={formInputClass} value={form.phone} onChange={update("phone")} data-testid="test-form-phone-input" />
      </FormField>
      <FormField label="Latest Date You'd Like Your Test By" error={errors.latestDate} testId="test-form-latest-date">
        <input type="date" className={formInputClass} value={form.latestDate} onChange={update("latestDate")} data-testid="test-form-latest-date-input" />
      </FormField>
      <FormField label="Suitable Dates" error={errors.suitableDates} testId="test-form-suitable-dates">
        <textarea rows={2} placeholder="e.g. Any weekday after 3pm" className={formInputClass} value={form.suitableDates} onChange={update("suitableDates")} data-testid="test-form-suitable-dates-input" />
      </FormField>
      <FormField label="Unsuitable Dates" testId="test-form-unsuitable-dates">
        <textarea rows={2} placeholder="e.g. 12th–15th (on holiday)" className={formInputClass} value={form.unsuitableDates} onChange={update("unsuitableDates")} data-testid="test-form-unsuitable-dates-input" />
      </FormField>
      {sendError && (
        <p data-testid="test-form-send-error" className="text-sm text-[#e08a6b] sm:col-span-2">
          {sendError}
        </p>
      )}
      <button
        type="submit"
        disabled={sending}
        data-testid="test-form-submit-button"
        className="mt-2 inline-flex w-full items-center justify-center gap-2 bg-[#c9a24b] px-8 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-[#0a0a09] transition-colors duration-200 hover:bg-[#e6c87a] disabled:cursor-not-allowed disabled:opacity-60 sm:col-span-2"
      >
        {sending ? "Sending…" : "Submit Enquiry"}
      </button>
    </form>
  );
};

export default function DrivingTests() {
  useSeo(
    "Driving Test Support | EDT Solutions",
    "Need an earlier driving test? EDT searches UK test centres for earlier practical test availability and supports you with simple, straightforward booking help."
  );
  return (
    <main data-testid="driving-tests-page">
      {/* Hero */}
      <section className="mx-auto max-w-6xl px-5 pb-16 pt-20 sm:px-8 sm:pb-20 sm:pt-28">
        <p className="section-label reveal" data-testid="dt-eyebrow">
          Driving Test Booking Support
        </p>
        <h1
          data-testid="dt-heading"
          className="font-display reveal mt-6 max-w-3xl text-4xl leading-[1.12] text-[#f2ede3] sm:text-5xl lg:text-6xl"
          style={{ animationDelay: "0.1s" }}
        >
          Need an <span className="gold-text">Earlier Driving Test?</span>
        </h1>
        <p
          data-testid="dt-subheading"
          className="reveal mt-6 max-w-xl text-base leading-relaxed text-[#a09a8c] sm:text-lg"
          style={{ animationDelay: "0.2s" }}
        >
          EDT helps you search for earlier practical driving test availability
          across UK test centres — with clear communication and straightforward
          booking support.
        </p>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="dt-whatsapp-button"
          className="reveal mt-10 inline-flex items-center gap-2 bg-[#c9a24b] px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-[#0a0a09] transition-colors duration-200 hover:bg-[#e6c87a]"
          style={{ animationDelay: "0.3s" }}
        >
          <MessageCircle size={16} />
          Message Us on WhatsApp
        </a>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
        <h2
          data-testid="benefits-heading"
          className="font-display text-3xl text-[#f2ede3] sm:text-4xl"
        >
          Why Use EDT
        </h2>
        <ul className="mt-10 divide-y divide-[rgba(201,162,75,0.15)] border-y border-[rgba(201,162,75,0.15)]">
          {benefits.map(({ icon: Icon, label }, i) => (
            <li
              key={label}
              data-testid={`benefit-${i + 1}`}
              className="flex items-center gap-4 py-4"
            >
              <Icon size={16} className="shrink-0 text-[#c9a24b]" />
              <span className="text-sm text-[#f2ede3]">{label}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-[#0e0d0c]">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
          <h2
            data-testid="how-it-works-heading"
            className="font-display text-2xl text-[#f2ede3] sm:text-[2rem]"
          >
            How It Works
          </h2>
          <div className="mt-10">
            <StepList steps={steps} testIdPrefix="dt" />
          </div>
          <p
            data-testid="dt-disclaimer"
            className="mt-10 max-w-2xl text-sm leading-relaxed text-[#6f6a5f]"
          >
            Please note: earlier test availability depends on cancellations and
            DVSA releases, so an earlier date cannot be guaranteed — but we search
            thoroughly on your behalf.
          </p>
        </div>
      </section>

      {/* Driving Test Booking Proof */}
      <BookingProofSection testId="dt-booking-proof" />

      {/* Before You Contact Us */}
      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
        <div className="border-y border-[rgba(201,162,75,0.18)] py-8">
          <div className="flex items-center gap-3">
            <ListChecks size={20} className="text-[#c9a24b]" />
            <h2
              data-testid="before-contact-heading"
              className="font-display text-2xl text-[#f2ede3] sm:text-3xl"
            >
              Before You Contact Us
            </h2>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-[#a09a8c]">
            Having the following ready helps us search for you faster:
          </p>
          <ul className="mt-7 grid gap-x-10 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {beforeYouContact.map(({ icon: Icon, label }, i) => (
              <li
                key={label}
                data-testid={`before-contact-item-${i + 1}`}
                className="flex items-center gap-3 text-sm text-[#a09a8c]"
              >
                <Icon size={16} className="shrink-0 text-[#c9a24b]" />
                {label}
              </li>
            ))}
          </ul>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="before-contact-whatsapp-button"
            className="mt-10 inline-flex items-center gap-2 border border-[rgba(201,162,75,0.5)] px-7 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-[#e6c87a] transition-colors duration-200 hover:bg-[#c9a24b] hover:text-[#0a0a09]"
          >
            <MessageCircle size={16} />
            Start on WhatsApp
          </a>
        </div>
      </section>

      {/* Test Enquiry Form */}
      <section id="test-form" className="border-t border-[rgba(201,162,75,0.22)] bg-[#111110] scroll-mt-20">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
          <p className="section-label" data-testid="test-form-label">
            Enquiry Form
          </p>
          <h2
            data-testid="test-form-heading"
            className="font-display mt-4 text-2xl leading-snug text-[#f2ede3] sm:text-[2rem]"
          >
            Request Driving Test Booking Support
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#a09a8c]">
            This is an enquiry form, not a live booking system. Fill it in and we&apos;ll
            be in touch to confirm availability.
          </p>
          <TestEnquiryForm />
        </div>
      </section>
    </main>
  );
}
